import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ButtonBase, Menu, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { TitleTypes } from 'features/co-pilot/co-pilot.types';
import _ from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import PaiDotLoader from './pai-dot-loader';
import { OptionType } from './pai-auto-complete';

interface Props {
  value: string | number;
  options: OptionType[];
  selectedValues?: string[];
  placeholder?: string;
  listItemActionText?: string;
  label?: string;
  loading?: boolean;
  disabled?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  disableBorder?: boolean;

  variant?: 'background' | 'white';
  multiselect?: boolean;

  style?: React.CSSProperties;

  onChange?(event: React.ChangeEvent<HTMLInputElement>): void;

  onKeyUp?(): void;

  onKeyDown?(event?: React.KeyboardEvent): void;

  onMenuItemClick?(value: string): () => void;
}

const PaiSearchSelector: React.FC<Props> = ({
  value,
  options,
  selectedValues,
  placeholder,
  listItemActionText,
  label,
  loading,
  disabled,
  variant = 'white',
  multiselect,
  style,
  onChange,
  onKeyUp,
  onKeyDown,
  onMenuItemClick,
}) => {
  const { classes, cx } = useStyles({ disabled, variant });
  const textFieldRef = useRef<any | null>(null);
  const hasMountedRef = useRef(false);

  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (hasMountedRef.current) {
      if (!_.isEmpty(options)) {
        setMenuOpen(true);
      } else {
        setMenuOpen(false);
      }
    } else {
      hasMountedRef.current = true;
    }
  }, [options]);

  const handleCloseMenu = () => {
    setMenuOpen(false);
  };

  const handleMenuItemClick = (value: string) => {
    return () => {
      if (!_.isNil(onMenuItemClick)) {
        onMenuItemClick(value)();
      }

      if (!multiselect) {
        handleCloseMenu();
      }
    };
  };

  return (
    <div className={classes.searchSelector}>
      {!_.isNil(label) ? <Typography className={classes.label}>{label}</Typography> : null}
      <TextField
        ref={textFieldRef}
        className={classes.textfield}
        InputProps={{
          disableUnderline: true,
          classes: { input: classes.fieldInput },
          endAdornment: loading ? (
            <div className={classes.iconContainer}>
              <PaiDotLoader size={'small'} />
            </div>
          ) : !_.isEmpty(options) ? (
            <ButtonBase className={classes.iconContainer} disableRipple={true} onClick={() => setMenuOpen((prev) => !prev)}>
              <KeyboardArrowDownIcon style={{ transform: menuOpen ? 'rotate(180deg)' : undefined }} />
            </ButtonBase>
          ) : null,
        }}
        variant={'standard'}
        fullWidth={true}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onKeyUp={onKeyUp}
        onKeyDown={onKeyDown}
        disabled={disabled}
        autoComplete={'off'}
        style={style}
      />
      <Menu
        className={classes.menu}
        classes={{ list: classes.menuList, paper: classes.menuPaper }}
        anchorEl={textFieldRef.current}
        open={menuOpen}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            width: textFieldRef?.current ? textFieldRef.current.clientWidth : 'auto',
          },
        }}
        disableEnforceFocus={true}
        disableAutoFocus={true}
        disableAutoFocusItem={true}
        disableRestoreFocus={true}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            className={cx(
              classes.menuItem,
              selectedValues?.some(
                (value) =>
                  value === option.value ||
                  value === option.label ||
                  (!_.isNil(value.split('::')[1]) && !_.isNil(option.value.split('::')[1]) && value.split('::')[1] === option.value.split('::')[1])
              ) && !multiselect
                ? 'selected'
                : undefined
            )}
            onClick={handleMenuItemClick(option.value)}
          >
            <Stack className={classes.menuItemContent} direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
              <Stack direction={'row'} gap={2}>
                {option.titleType === TitleTypes.OCCUPATION ? <FormatListBulletedIcon className={classes.checkBoxIcon} /> : null}
                {multiselect ? (
                  selectedValues?.some((value) => value === option.value) ? (
                    <CheckBoxIcon className={classes.checkBoxIcon} />
                  ) : (
                    <CheckBoxOutlineBlankIcon className={classes.checkBoxIcon} />
                  )
                ) : null}
                <Typography className={classes.menuItemText}>{option.label}</Typography>
              </Stack>
              {!_.isNil(listItemActionText) ? <Typography className={classes.menuItemActionText}>{listItemActionText}</Typography> : null}
            </Stack>
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

const useStyles = makeStyles<{ disabled?: boolean; variant?: 'background' | 'white' }>()((theme, { disabled, variant }) => ({
  searchSelector: {
    width: '100%',
  },
  textfield: {
    border:
      variant !== 'background'
        ? disabled
          ? `1px solid ${theme.palette.action.disabled}`
          : `1px solid ${theme.palette.primary.main}`
        : undefined,

    borderRadius: '10px',
    background: variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF',
    '& .MuiSvgIcon-root': {
      color: theme.palette.primary.main,
      height: '100%',
      top: 0,
      background: '#FFF',
    },
  },
  fieldInput: {
    fontSize: theme.typography.pxToRem(15),
    color: disabled ? theme.palette.greys.light : undefined,
    padding: '8px 12px',
    minHeight: 'calc(39px - 8px - 8px)',
    background: variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF',
    borderRadius: '10px',
    '&::placeholder': {
      opacity: 0.5,
      fontSize: theme.typography.pxToRem(15),
    },
    '&:focus': {
      '&::placeholder': {
        opacity: 0,
      },
    },
  },
  iconContainer: {
    minHeight: '39px',
    borderRadius: '10px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF',
    padding: '0 8px',
  },
  label: {
    marginBottom: '8px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  menu: {
    zIndex: 1350,
  },
  menuPaper: {
    borderRadius: '6px',
    maxHeight: '33vh',
    width: '100%',
    marginTop: '8px',
  },
  menuList: {
    padding: 0,
  },
  checkBoxIcon: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.primary.main,
  },
  menuItem: {
    fontSize: theme.typography.pxToRem(15),
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    padding: '6px 16px',
    '&:last-of-type': {
      borderBottom: 'unset',
    },
    '&.Mui-selected': {
      background: theme.palette.backgrounds.main,

      '&:hover, &:focus': {
        background: theme.palette.backgrounds.main,
      },
    },
    '&:hover': {
      background: theme.palette.backgrounds.main,
    },
    '&:focus': {
      background: theme.palette.backgrounds.main,
    },
  },
  menuItemContent: {
    width: '100%',
  },
  menuItemText: {
    fontSize: theme.typography.pxToRem(13),
    textTransform: 'capitalize',
  },
  menuItemActionText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.primary.main,
    textTransform: 'lowercase',
  },
}));

export default PaiSearchSelector;
